<template>
    <v-row>
        <v-col cols="12">
            <company-autocomplete
                ref="companyAutocomplete"
                v-model="selectedCompany"
                :label="`${$t('choose.company_long')}*`"
                :types="isTO1 ? ['TOUR_OPERATOR', 'TOUR_AGENCY'] : isTO2 ? ['TOUR_AGENCY'] : []"
                phone-search
                active-only
                outlined
            />
        </v-col>
        <v-col cols="12">
            <client-autocomplete
                v-if="!newAgentMode"
                ref="clients"
                v-model="selectedClient"
                :label="`${$t('choose.agent_long')}*`"
                :company-id="selectedCompany && selectedCompany.id"
                :is-ta="true"
                :disabled="!selectedCompany"
                outlined
            >
                <v-list-item ripple @mousedown.prevent @click="onNewAgentClick">
                    <v-list-item-title class="text-primary font-weight-medium">
                        {{ $t('create_new_agent') }}
                    </v-list-item-title>
                </v-list-item>
            </client-autocomplete>
        </v-col>
        <v-col v-if="!hideNewClient" cols="12">
            <div class="d-flex align-center mb-6 full-width">
                <v-spacer />
                <v-btn
                    elevation="0"
                    @click="selectedExistedClientMode = !selectedExistedClientMode"
                    v-text="
                        !selectedExistedClientMode
                            ? `${$t('choose.choose')} ${$t('choose.client')}`
                            : $t('new_client_profile')
                    "
                />
            </div>
            <client-autocomplete
                v-if="selectedExistedClientMode === true"
                ref="clientAutocomplete"
                v-model="selectedExistedClient"
                :company-id="selectedCompany && selectedCompany.id"
                :label="`${$t('choose.client_long')}*`"
                outlined
                :disabled="!selectedCompany"
            />
            <div v-else class="d-flex flex-column">
                <v-select
                    v-model="newClient.prefix"
                    :items="['Mr', 'Ms', 'Mrs']"
                    persistent-placeholder
                    variant="outlined"
                    :density="'comfortable'"
                    :label="`${$t('salutation')}*`"
                    :rules="[requiredRule('salutation')]"
                />
                <v-text-field
                    v-model="newClient.firstName"
                    :label="`${$t('first_name')}*`"
                    persistent-placeholder
                    variant="outlined"
                    :density="'comfortable'"
                    :rules="[requiredRule('first_name')]"
                />
                <v-text-field
                    v-model="newClient.lastName"
                    :label="`${$t('last_name')}*`"
                    persistent-placeholder
                    variant="outlined"
                    :density="'comfortable'"
                    :rules="[requiredRule('last_name')]"
                />
            </div>
        </v-col>
    </v-row>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Watch} from 'vue-facing-decorator'
    import CompanyAutocomplete from '@/components/account/modals/newOrderRequest/CompanyAutocomplete.vue'
    import ClientAutocomplete from '@/components/account/modals/newOrderRequest/ClientAutocomplete.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import PersonForm from '~src/components/account/forms/personForm.src'
    import PrivateClient from '~/components/account/modals/newOrderRequest/forms/PrivateClient.vue'

    @Component({
        components: {PrivateClient, PersonForm, ClientAutocomplete, CompanyAutocomplete},
    })
    export class TourAgency extends mixins(FormValidationRulesMixin) {
        @Prop({default: false, type: Boolean}) hideNewClient

        @Prop() _selectedCompany
        @Prop() _selectedClient
        @Prop() _newClient
        @Prop() _newAgentMode

        firstName = null
        lastName = null

        selectedExistedClient = null
        selectedExistedClientMode = true

        adaptNewClient() {
            if (this.selectedExistedClientMode) {
                this.newClient = this.selectedExistedClient?.personId
                    ? {personId: this.selectedExistedClient?.personId}
                    : {
                          prefix: this.selectedExistedClient?.prefix || null,
                          firstName: this.selectedExistedClient?.firstName || null,
                          lastName: this.selectedExistedClient?.lastName || null,
                      }
            } else {
                this.newClient = {
                    prefix: this.selectedExistedClient?.prefix || null,
                    firstName: this.selectedExistedClient?.firstName || null,
                    lastName: this.selectedExistedClient?.lastName || null,
                }
            }
        }

        @Watch('selectedExistedClientMode')
        onSelectedExistedClientModeChange() {
            this.adaptNewClient()
        }

        @Watch('selectedExistedClient')
        onSelectedExistedClientChange() {
            this.onSelectedExistedClientModeChange()
        }

        @Emit()
        changeSelectedCompany(val) {
            return val
        }

        @Emit()
        changeSelectedClient(val) {
            return val
        }

        @Emit()
        changeNewClient(val) {
            return val
        }

        @Emit()
        async changeNewAgentMode(val) {
            if (!this.newAgentMode) {
                await this.$nextTick(() => {})
                this.$refs.clients?.onInput('')
            }

            return val
        }

        onNewAgentClick() {
            this.newAgentMode = true
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get selectedCompany() {
            return this._selectedCompany
        }

        set selectedCompany(val) {
            this.changeSelectedCompany(val)
        }

        get selectedClient() {
            return this._selectedClient
        }

        set selectedClient(val) {
            this.changeSelectedClient(val)
        }

        get newClient() {
            return this._newClient
        }

        set newClient(val) {
            this.changeNewClient(val)
        }

        get newAgentMode() {
            return this._newAgentMode
        }

        set newAgentMode(val) {
            this.changeNewAgentMode(val)
        }
    }

    export default TourAgency
</script>
